import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export const SessionManager = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [settings, setSettings] = useState({ timeout_hours: 8, warning_minutes: 5 });
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Fetch session settings
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/settings/session`).then(r => setSettings(r.data)).catch(() => {});
  }, [token]);

  const timeoutMs = settings.timeout_hours * 60 * 60 * 1000;
  const warningMs = settings.warning_minutes * 60 * 1000;

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showWarning) {
      setShowWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [showWarning]);

  // Silent token refresh every 30 min
  useEffect(() => {
    if (!token || !user) return;
    const doRefresh = async () => {
      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`);
        if (data.token) {
          sessionStorage.setItem('token', data.token);
          localStorage.setItem('token', data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }
      } catch {}
    };
    refreshTimerRef.current = setInterval(doRefresh, 30 * 60 * 1000);
    return () => clearInterval(refreshTimerRef.current);
  }, [token, user]);

  // Track activity
  useEffect(() => {
    if (!user || !token || settings.timeout_hours === 0) return;

    const handleActivity = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    // Check inactivity every 30s
    warningTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = timeoutMs - elapsed;

      if (remaining <= 0) {
        // Time's up — logout
        clearInterval(warningTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        logout();
        navigate('/login', { state: { message: 'Sesja wygasła. Zaloguj się ponownie.' } });
      } else if (remaining <= warningMs && !showWarning) {
        // Show warning
        setShowWarning(true);
        setCountdown(Math.ceil(remaining / 1000));
        countdownRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownRef.current);
              logout();
              navigate('/login', { state: { message: 'Sesja wygasła. Zaloguj się ponownie.' } });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }, 30000);

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
      if (warningTimerRef.current) clearInterval(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, token, settings, timeoutMs, warningMs, logout, navigate, showWarning]);

  if (!showWarning) return null;

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center" data-testid="session-warning">
      <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-xl shadow-2xl p-6 w-[400px] max-w-[90vw]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Nieaktywność</h3>
            <p className="text-[var(--crm-text-secondary)] text-sm">Sesja wkrótce wygaśnie</p>
          </div>
        </div>
        <p className="text-[var(--crm-text-secondary)] text-sm mb-4">
          Zostaniesz wylogowany za <span className="text-amber-400 font-bold font-mono">{mins}:{secs.toString().padStart(2, '0')}</span> z powodu braku aktywności.
        </p>
        <button onClick={resetActivity} data-testid="session-stay-btn"
          className="w-full bg-[#FF6B35] hover:bg-[#e55a2a] text-white py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2">
          <Clock className="w-4 h-4" />
          Pozostań zalogowany
        </button>
      </div>
    </div>
  );
};
